import type {
  ApiError,
  AssetListResponse,
  AssetRegistration,
  AssetUpdate,
  UploadRequest,
  UploadResponse,
  ExhibitionInput,
  ExhibitionResponse,
  ListResponse,
  PublishResponse,
  PublishStatusResponse,
  RevisionsResponse,
  WorkInput,
  WorkResponse,
  WorkUpdate,
} from "@yushi-portfolio/api-types";
import type { Asset, ExhibitionDocument, WorkDocument } from "@yushi-portfolio/content-schema";
import type { Auth } from "./auth";

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: string[] = [],
  ) {
    super(message);
  }
}

export function createApiClient(baseUrl: string, auth: Auth) {
  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const token = await auth.getAccessToken();
    const res = await fetch(`${baseUrl}${path}`, {
      method,
      headers: {
        ...(body !== undefined ? { "content-type": "application/json" } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    const text = await res.text();
    const json = text ? (JSON.parse(text) as unknown) : undefined;
    if (!res.ok) {
      const error = (json as ApiError | undefined)?.error;
      throw new ApiRequestError(res.status, error?.code ?? "http_error", error?.message ?? `HTTP ${res.status}`, error?.details);
    }
    return json as T;
  }

  return {
    listWorks: () => request<ListResponse>("GET", "/works"),
    getWork: (id: string) => request<WorkResponse>("GET", `/works/${encodeURIComponent(id)}`),
    createWork: (body: WorkInput) => request<WorkResponse>("POST", "/works", body),
    updateWork: (id: string, body: WorkUpdate) => request<WorkResponse>("PUT", `/works/${encodeURIComponent(id)}`, body),

    publishWork: (id: string, version: number) => request<PublishResponse<WorkDocument>>("POST", `/works/${encodeURIComponent(id)}/publish`, { version }),
    publishExhibition: (id: string) => request<PublishResponse<ExhibitionDocument>>("POST", `/exhibitions/${encodeURIComponent(id)}/publish`, {}),
    revisions: (kind: "works" | "exhibitions", id: string) => request<RevisionsResponse>("GET", `/${kind}/${encodeURIComponent(id)}/revisions`),
    publishStatus: () => request<PublishStatusResponse>("GET", "/publish/status"),

    listAssets: () => request<AssetListResponse>("GET", "/assets"),
    getAsset: (id: string) => request<Asset>("GET", `/assets/${encodeURIComponent(id)}`),
    createUpload: (body: UploadRequest) => request<UploadResponse>("POST", "/assets/uploads", body),
    registerAsset: (body: AssetRegistration) => request<Asset>("POST", "/assets", body),
    updateAsset: (id: string, body: AssetUpdate) => request<Asset>("PUT", `/assets/${encodeURIComponent(id)}`, body),

    listExhibitions: () => request<ListResponse>("GET", "/exhibitions"),
    getExhibition: (id: string) => request<ExhibitionResponse>("GET", `/exhibitions/${encodeURIComponent(id)}`),
    createExhibition: (body: ExhibitionInput) => request<ExhibitionResponse>("POST", "/exhibitions", body),
    updateExhibition: (id: string, body: ExhibitionInput) =>
      request<ExhibitionResponse>("PUT", `/exhibitions/${encodeURIComponent(id)}`, body),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
